import * as Dialog from '@radix-ui/react-dialog';
import { SlidersHorizontal, X } from 'lucide-react';
import { useLayoutEffect, useRef, type ReactNode } from 'react';

import { SaveState } from '../../components/SaveState.js';
import type { AutosaveStatus } from '../../lib/autosave.js';
import { useDianaUIPortal } from '../../ui/index.js';
import { blurPageEditorFocus } from './PageSectionsWorkspace.js';

export function PageSettingsPanel({
  open,
  onOpenChange,
  conflicted,
  title,
  status,
  onRetry,
  children,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  conflicted: boolean;
  title: string;
  status: AutosaveStatus;
  onRetry?(): void;
  children: ReactNode;
}) {
  const portal = useDianaUIPortal();
  const trigger = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    if (!conflicted || !open) return;
    // Commit local placement alt before releasing the settings subtree for conflict actions.
    blurPageEditorFocus();
    onOpenChange(false);
  }, [conflicted, open, onOpenChange]);
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        blurPageEditorFocus();
        onOpenChange(next);
      }}
    >
      <Dialog.Trigger ref={trigger} className="page-editor__settings" aria-label="Page settings">
        <SlidersHorizontal size={19} aria-hidden="true" /> Page settings
      </Dialog.Trigger>
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="page-settings__overlay" />
        <Dialog.Content
          className="page-settings"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (!conflicted) trigger.current?.focus();
          }}
        >
          <header className="page-settings__header">
            <div>
              <p>{title}</p>
              <Dialog.Title>Page settings</Dialog.Title>
            </div>
            <Dialog.Close className="page-settings__close" aria-label="Close page settings">
              <X size={20} aria-hidden="true" />
            </Dialog.Close>
          </header>
          <Dialog.Description className="sr-only">
            Website access, search appearance and social sharing. Changes save automatically.
          </Dialog.Description>
          <div className="page-settings__body">{children}</div>
          <footer className="page-settings__footer">
            <div className="page-settings__status">
              <SaveState status={status} />
              {status === 'Error' && onRetry ? (
                <button type="button" className="page-settings__retry" onClick={onRetry}>
                  Retry save
                </button>
              ) : null}
            </div>
            <Dialog.Close className="page-settings__done">Done</Dialog.Close>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
